import React, {
  Suspense,
  lazy,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { css } from "@emotion/css";
import type {
  SessionScope,
  UiInteractionSpec,
  CoachMessageUiInteractionSpec,
  ConceptDiagramUiInteractionSpec,
  CustomSvgUiInteractionSpec,
  LearnerMessageUiInteractionSpec,
  ManimAnimationUiInteractionSpec,
} from "../../types/state";
import { MathMarkdown } from "../../components/MathMarkdown";
import { PendingIndicator } from "../../components/PendingIndicator";
import { SelectionPopover } from "./SelectionPopover";

// A deep-dive turn may answer in prose, in a diagram, or in an
// animation.  Only prose is on the common path, so the two visual
// renderers (React Flow, the video player) load on demand — their
// Suspense fallback is the first of this feature's loading states.
const ConceptDiagram = lazy(() => import("./interactions/ConceptDiagram"));
const CustomSvgFigure = lazy(() => import("./interactions/CustomSvgFigure"));
const ManimPlayer = lazy(() => import("./interactions/ManimPlayer"));

/** The prose of a coach turn, whatever shape it answered in. */
const turnText = (it: UiInteractionSpec): string =>
  (it as CoachMessageUiInteractionSpec).message ??
  (it as ManimAnimationUiInteractionSpec).explanation ??
  "";

/** The scope's label, short enough to sit on one waiting row.
 *
 *  A dive opens from a SELECTION, and a learner can select a whole
 *  sentence as easily as a term — the breadcrumb clamps that in CSS, and
 *  a row that says "Looking into …" has the same problem.  Cut on a word
 *  boundary, so what survives still reads as words rather than as a
 *  typo. */
export function shortLabel(label: string, limit = 42): string {
  const trimmed = (label ?? "").trim();
  if (trimmed.length <= limit) return trimmed;
  const cut = trimmed.slice(0, limit);
  const lastSpace = cut.lastIndexOf(" ");
  return `${(lastSpace > limit * 0.5 ? cut.slice(0, lastSpace) : cut).trimEnd()}…`;
}

/**
 * The deep-dive scope view: a focused explanation thread on one concept.
 * Coach explanations + the learner's questions render in order; the
 * learner can highlight any term to nest ANOTHER dive.  The bottom row
 * is the deep-dive control set: Back to lesson · Ask · More.
 *
 * A coach turn may answer in prose (``coach_message``), in prose plus a
 * figure the coach drew (``custom_svg``), in prose plus a node-and-edge
 * map (``concept_diagram``), or in prose plus an animation
 * (``manim_animation``) — the coach picks the shape the concept earns.
 * All three render as one explanation block here: the prose first, its
 * visual underneath, and a pending state in the visual's place until it
 * arrives.
 */

const rootStyle = css`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
`;

const scrollWrapStyle = css`
  position: relative;
  flex: 1;
  min-height: 0;
  display: flex;
`;

const scrollStyle = css`
  position: relative;
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const fadeBaseStyle = css`
  position: absolute;
  left: 0;
  right: 0;
  height: 32px;
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.18s ease;
  z-index: 2;
`;

const topFadeStyle = css`
  top: 0;
  background: linear-gradient(to bottom, var(--color-bg), transparent);
`;

const bottomFadeStyle = css`
  bottom: 0;
  background: linear-gradient(to top, var(--color-bg), transparent);
`;

const fadeVisibleStyle = css`
  opacity: 1;
`;

const moreCueStyle = css`
  position: absolute;
  left: 50%;
  bottom: 8px;
  transform: translateX(-50%);
  z-index: 3;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 10px;
  border-radius: 999px;
  border: 1px solid var(--color-border-subtle);
  background: var(--color-bg-elevated);
  color: var(--color-text-muted);
  font-size: 0.72em;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 2px 8px -4px var(--color-shadow-strong, rgba(0, 0, 0, 0.4));
  animation: ddMoreCue 1.6s ease-in-out infinite;

  @keyframes ddMoreCue {
    0%, 100% { transform: translateX(-50%) translateY(0); }
    50% { transform: translateX(-50%) translateY(2px); }
  }
`;

const explanationStyle = css`
  font-size: 0.97em;
  color: var(--color-text);
  line-height: 1.65;

  p { margin: 0.5em 0; }
  p:first-of-type { margin-top: 0; }
  p:last-of-type { margin-bottom: 0; }
  strong { font-weight: 600; }
  code {
    background: var(--color-bg-raised);
    padding: 1px 5px;
    border-radius: 4px;
    font-size: 0.92em;
  }
`;

const visualStyle = css`
  margin-top: 12px;
`;

// The waiting row.  Sits where the turn will land, so the thread grows
// downward from it rather than jumping when the turn takes its place.
const awaitingStyle = css`
  padding: 4px 0;
  color: var(--color-text-muted);
  font-size: 0.92em;
`;

const questionStyle = css`
  align-self: flex-end;
  max-width: 86%;
  padding: 9px 14px;
  border-radius: 14px;
  border-bottom-right-radius: 4px;
  background: var(--color-primary-muted);
  color: var(--color-text);
  font-size: 0.92em;
  line-height: 1.5;
  white-space: pre-wrap;
`;

const barStyle = css`
  flex-shrink: 0;
  display: flex;
  gap: 8px;
  padding: 10px 14px calc(10px + env(safe-area-inset-bottom, 0px));
  border-top: 1px solid var(--color-border-subtle);
  background: color-mix(in srgb, var(--color-bg) 90%, transparent);
  backdrop-filter: blur(6px);
`;

const barBtnStyle = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 9px 12px;
  border-radius: 10px;
  border: 1px solid var(--color-border-subtle);
  background: transparent;
  color: var(--color-text-muted);
  font-size: 0.88em;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;

  &:hover:not(:disabled) {
    border-color: var(--color-border);
    background: var(--color-bg-subtle);
    color: var(--color-text);
  }
  &:disabled { opacity: 0.45; cursor: not-allowed; }
`;

const backBtnStyle = css`${barBtnStyle}; flex: 0 0 auto;`;
const askBtnStyle = css`${barBtnStyle}; flex: 1;`;
const moreBtnStyle = css`
  ${barBtnStyle};
  flex: 0 0 auto;
  border-color: var(--color-primary);
  color: var(--color-primary);
  &:hover:not(:disabled) { background: var(--color-primary-muted); color: var(--color-primary); }
`;

const composerStyle = css`
  flex: 1;
  display: flex;
  gap: 8px;
  align-items: flex-end;
`;

const textareaStyle = css`
  flex: 1;
  min-height: 40px;
  max-height: 120px;
  border: 1px solid var(--color-border);
  border-radius: 12px;
  padding: 9px 12px;
  background: var(--color-bg-elevated);
  color: var(--color-text);
  font-family: inherit;
  font-size: 0.92em;
  line-height: 1.4;
  box-sizing: border-box;
  resize: none;

  &:focus { outline: none; border-color: var(--color-primary); }
  &::placeholder { color: var(--color-text-muted); }
`;

const sendBtnStyle = css`
  flex-shrink: 0;
  padding: 9px 16px;
  border: none;
  border-radius: 12px;
  background: var(--color-primary);
  color: var(--color-text-inverse);
  font-size: 0.9em;
  font-weight: 600;
  cursor: pointer;
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

interface DeepDiveViewProps {
  scope: SessionScope;
  isStreaming: boolean;
  onBack: () => void;
  onMore: () => void;
  onAsk: (text: string) => void;
  onDiveDeeper: (text: string) => void;
}

export const DeepDiveView: React.FC<DeepDiveViewProps> = ({
  scope,
  isStreaming,
  onBack,
  onMore,
  onAsk,
  onDiveDeeper,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [asking, setAsking] = useState(false);
  const [draft, setDraft] = useState("");
  const [moreBelow, setMoreBelow] = useState(false);
  const [moreAbove, setMoreAbove] = useState(false);

  const order = scope.work.interactionOrder;
  const interactions = scope.work.interactions;

  // The newest coach turn — what a new generation anchors to.
  const lastCoachId = (() => {
    for (let i = order.length - 1; i >= 0; i--) {
      if (interactions[order[i]]?.kind !== "learner_message") return order[i];
    }
    return undefined;
  })();
  const lastTurn = lastCoachId ? interactions[lastCoachId] : undefined;
  const lastLen = lastTurn ? turnText(lastTurn).length : 0;

  // The coach owes this thread a turn that has not begun to arrive.
  //
  // A turn's own pending state can only render once its SHELL exists,
  // and the shell is written when the model's first token lands — which
  // on a reasoning model is seconds after the tap.  Until then the
  // thread has nothing to put it on: empty on the dive that opened the
  // scope, and ending in a frozen turn or the learner's own question on
  // a "More" or an "Ask".  So the wait read as nothing happening.
  //
  // Derived, never a flag: when the stream ends this goes false on its
  // own, so it cannot strand a spinner on a turn that failed.  An open
  // turn (``status`` still ``active``) means the shell is there and the
  // per-turn indicator has it — which is also why navigating back into a
  // finished scope shows nothing: its last turn is still the open one.
  const lastEntry = order.length ? interactions[order[order.length - 1]] : undefined;
  const hasOpenTurn =
    !!lastEntry && lastEntry.kind !== "learner_message" && lastEntry.status === "active";
  const awaitingTurn = isStreaming && !hasOpenTurn;
  // No trailing ellipsis on the quoted form: the label may already end
  // in one from being cut, and the spinner beside it is what says "in
  // progress" anyway.
  const awaitingLabel =
    order.length === 0 ? `Looking into “${shortLabel(scope.label)}”` : "Thinking…";

  const recomputeOverflow = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setMoreAbove(el.scrollTop > 8);
    setMoreBelow(el.scrollHeight - el.scrollTop - el.clientHeight > 8);
  }, []);

  // Sticky top-anchor.  When a new coach turn arrives we pin ITS top near
  // the viewport top — where reading starts — and HOLD it there as the
  // text streams in below (rather than chasing the bottom).  Holding
  // matters because the turn starts empty: there isn't enough content to
  // scroll its top up until it has streamed in, so we keep re-applying.
  // The learner can scroll away at any time to take over.
  const anchorRef = useRef<{ id: string; taken: boolean } | null>(null);
  const forcedTop = useRef(0);
  const prevCoachId = useRef<string | undefined>(undefined);

  const applyAnchor = useCallback(() => {
    const el = scrollRef.current;
    const a = anchorRef.current;
    if (!el || !a || a.taken) {
      recomputeOverflow();
      return;
    }
    const target = el.querySelector<HTMLElement>(`[data-msg-id="${a.id}"]`);
    if (target) {
      const want = Math.max(0, target.offsetTop - 12);
      if (Math.abs(el.scrollTop - want) > 1) el.scrollTop = want;
      forcedTop.current = el.scrollTop;
    }
    recomputeOverflow();
  }, [recomputeOverflow]);

  // Start a fresh anchor on each new coach turn.
  useLayoutEffect(() => {
    if (lastCoachId && lastCoachId !== prevCoachId.current) {
      prevCoachId.current = lastCoachId;
      anchorRef.current = { id: lastCoachId, taken: false };
      applyAnchor();
    }
  }, [lastCoachId, applyAnchor]);

  // Hold the anchor as the turn streams, and settle once it finishes.
  useLayoutEffect(() => {
    applyAnchor();
  }, [lastLen, order.length, isStreaming, applyAnchor]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onScroll = () => {
      recomputeOverflow();
      const a = anchorRef.current;
      if (a && !a.taken && Math.abs(el.scrollTop - forcedTop.current) > 3) {
        a.taken = true; // the learner took over scrolling
      }
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    const ro = new ResizeObserver(() => applyAnchor());
    ro.observe(el);
    return () => {
      el.removeEventListener("scroll", onScroll);
      ro.disconnect();
    };
  }, [recomputeOverflow, applyAnchor]);

  const scrollToBottom = () => {
    if (anchorRef.current) anchorRef.current.taken = true;
    const el = scrollRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
  };

  const send = () => {
    const text = draft.trim();
    if (!text || isStreaming) return;
    onAsk(text);
    setDraft("");
    setAsking(false);
  };

  return (
    <div className={rootStyle}>
      <div className={scrollWrapStyle}>
        <SelectionPopover onDiveDeeper={onDiveDeeper} disabled={isStreaming}>
          <div className={scrollStyle} ref={scrollRef} data-testid="deep-dive-thread">
            {order.map((id, idx) => {
              const it = interactions[id];
              if (!it) return null;
              if (it.kind === "learner_message") {
                const lm = it as LearnerMessageUiInteractionSpec;
                return (
                  <div
                    key={id}
                    data-msg-id={id}
                    className={questionStyle}
                    data-testid="deep-dive-question"
                  >
                    {lm.text}
                  </div>
                );
              }
              // A coach turn: prose, prose + diagram, or prose +
              // animation.  Each shape's visual half streams in AFTER
              // its explanation, so each carries its own pending state
              // rather than holding the text back until it lands.
              const body = turnText(it);
              const streaming = idx === order.length - 1 && isStreaming;
              return (
                <div
                  key={id}
                  data-msg-id={id}
                  className={explanationStyle}
                  data-testid="deep-dive-explanation"
                >
                  {body ? (
                    <MathMarkdown>{body}</MathMarkdown>
                  ) : streaming ? (
                    <PendingIndicator variant="inline" label="Explaining…" />
                  ) : null}
                  {it.kind === "custom_svg" ? (
                    <div className={visualStyle}>
                      <Suspense
                        fallback={
                          <PendingIndicator variant="block" label="Drawing the figure…" />
                        }
                      >
                        <CustomSvgFigure
                          figure={(it as CustomSvgUiInteractionSpec).figure}
                          pending={streaming}
                        />
                      </Suspense>
                    </div>
                  ) : it.kind === "concept_diagram" ? (
                    <div className={visualStyle}>
                      <Suspense
                        fallback={
                          <PendingIndicator variant="block" label="Drawing the diagram…" />
                        }
                      >
                        <ConceptDiagram
                          diagram={(it as ConceptDiagramUiInteractionSpec).diagram}
                          pending={streaming}
                        />
                      </Suspense>
                    </div>
                  ) : it.kind === "manim_animation" ? (
                    <div className={visualStyle}>
                      <Suspense
                        fallback={
                          <PendingIndicator variant="block" label="Preparing the animation…" />
                        }
                      >
                        <ManimPlayer
                          animation={(it as ManimAnimationUiInteractionSpec).animation}
                        />
                      </Suspense>
                    </div>
                  ) : null}
                </div>
              );
            })}
            {awaitingTurn && (
              <div className={awaitingStyle} data-testid="deep-dive-awaiting">
                <PendingIndicator variant="block" label={awaitingLabel} />
              </div>
            )}
          </div>
        </SelectionPopover>

        <div
          className={`${fadeBaseStyle} ${topFadeStyle} ${moreAbove ? fadeVisibleStyle : ""}`}
          aria-hidden="true"
        />
        <div
          className={`${fadeBaseStyle} ${bottomFadeStyle} ${moreBelow ? fadeVisibleStyle : ""}`}
          aria-hidden="true"
        />
        {moreBelow && (
          <button
            type="button"
            className={moreCueStyle}
            onClick={scrollToBottom}
            data-testid="deep-dive-more-below"
          >
            More below <span aria-hidden="true">⌄</span>
          </button>
        )}
      </div>

      <div className={barStyle} data-testid="deep-dive-bar">
        {asking ? (
          <div className={composerStyle}>
            <textarea
              className={textareaStyle}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  send();
                }
                if (e.key === "Escape") setAsking(false);
              }}
              placeholder="Ask about this concept…"
              rows={1}
              autoFocus
              data-testid="deep-dive-ask-input"
            />
            <button
              type="button"
              className={sendBtnStyle}
              onClick={send}
              disabled={isStreaming || !draft.trim()}
              data-testid="deep-dive-ask-send"
            >
              Send
            </button>
          </div>
        ) : (
          <>
            <button
              type="button"
              className={backBtnStyle}
              onClick={onBack}
              data-testid="deep-dive-back"
            >
              <span aria-hidden="true">‹</span> Back to lesson
            </button>
            <button
              type="button"
              className={askBtnStyle}
              onClick={() => setAsking(true)}
              data-testid="deep-dive-ask"
            >
              <span aria-hidden="true">💬</span> Ask
            </button>
            <button
              type="button"
              className={moreBtnStyle}
              onClick={onMore}
              disabled={isStreaming}
              data-testid="deep-dive-more"
            >
              More…
            </button>
          </>
        )}
      </div>
    </div>
  );
};
